"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.redshiftSchema = exports.microsoftSQLServerSchema = exports.snowflakeSchema = exports.mySQLSchema = exports.mariadbSchema = exports.postgresSchema = exports.bigQuerySchema = exports._sqlDataSetSchema = exports._fieldSchema = exports.ID_REGEX_ERROR_MESSAGE = exports.ALLOWED_ID_REGEX = void 0;
const joi_1 = __importDefault(require("@vizzly/joi"));
const getQueryEngineConfig_1 = require("../QueryEngineConfig/getQueryEngineConfig");
const constants_1 = require("./constants");
const support_1 = require("../QueryEngineConfig/support");
const constants_2 = require("../QueryEngineConfig/constants");
const DataSetNotLinkedToConnection_1 = require("../errors/DataSetNotLinkedToConnection");
const REDACT_KEYS_FROM_ERROR_MESSAGE = ['unencryptedCredentials', 'encryptedCredentials'];
const SUPPORTED_SECURE_FILTER_OPS = Object.keys(constants_2.supportedOperatorsConstants);
const SUPPORTED_ALLOWED_GRANULARITIES = [
    'second',
    'minute',
    'hour',
    'day',
    'week',
    'month',
    'quarter',
    'year',
    'dayOfMonthOnly',
    'dayOfWeekOnly',
    'weekOfYearOnly',
    'hourOfDayOnly',
];
const SUPPORTED_JOINS = ['rightJoin', 'leftJoin', 'innerJoin', 'outerJoin', 'leftOuterJoin', 'rightOuterJoin'];
exports.ALLOWED_ID_REGEX = /^[a-zA-Z0-9_+:\/=\-\s]*$/;
exports.ID_REGEX_ERROR_MESSAGE = 'IDs must only use alpha numeric characters and _';
const DEFAULT_SCHEMA_OPTIONS = { checkConnectionSpecificDataSets: true };
const joiOptions = { abortEarly: false, allowUnknown: true };
const _fieldSchema = (client) => {
    return joi_1.default.object({
        id: joi_1.default.string()
            .required()
            .regex(exports.ALLOWED_ID_REGEX, exports.ID_REGEX_ERROR_MESSAGE)
            .description('A unique identifier for this field. It should not change unless the meaning of the field, or the data type has changed.'),
        publicName: joi_1.default.string()
            .optional()
            .allow('')
            .description('The name of this field that you want your users to see. This can be changed as much as you like without causing a breaking change.'),
        address: joi_1.default.array()
            .items(joi_1.default.string())
            .optional()
            .description('The address of the column in your database. For example; ["database_name", "table_name", "column_name"] or for BigQuery; ["bigquery project id", "bigquery data set id", "table name", "column name"]'),
        description: joi_1.default.string()
            .allow('')
            .optional()
            .max(110)
            .description('A description of what the purpose of this data set is'),
        sql: joi_1.default.string()
            .optional()
            .description('Provide a SQL definition of the field, instead of a table & name combination.'),
        dataType: joi_1.default.valid(...Object.values(constants_1.NATIVE_DATA_TYPES[client])).required(),
        canBeDimension: joi_1.default.boolean().optional().description('Can this field be used in groupings?'),
        canBeMeasure: joi_1.default.boolean().optional().description('Can this field be used in aggregations?'),
        canBeUsedInSort: joi_1.default.boolean().optional().description('__vizzly_hide_from_docs'),
        allowedGranularities: joi_1.default.array()
            .items(joi_1.default.string().valid(...SUPPORTED_ALLOWED_GRANULARITIES))
            .optional(),
        allowedOperators: joi_1.default.alternatives()
            .conditional('..dataType', {
            is: joi_1.default.string().valid(...(0, constants_1.vizzlyToPossibleNativeDataTypes)(client, 'string')),
            then: joi_1.default.array()
                .items(joi_1.default.string().valid(...(0, support_1.validOperatorsForField)({ dataType: 'string' }, (0, getQueryEngineConfig_1.getQueryEngineConfig)(client))))
                .description('For fields that have a string data type.'),
        })
            .conditional('..dataType', {
            is: joi_1.default.string().valid(...(0, constants_1.vizzlyToPossibleNativeDataTypes)(client, 'number')),
            then: joi_1.default.array()
                .items(joi_1.default.string().valid(...(0, support_1.validOperatorsForField)({ dataType: 'number' }, (0, getQueryEngineConfig_1.getQueryEngineConfig)(client))))
                .description('For fields that have a number data type.'),
        })
            .conditional('..dataType', {
            is: joi_1.default.string().valid(...(0, constants_1.vizzlyToPossibleNativeDataTypes)(client, 'date_time')),
            then: joi_1.default.array()
                .items(joi_1.default.valid(...(0, support_1.validOperatorsForField)({ dataType: 'date_time' }, (0, getQueryEngineConfig_1.getQueryEngineConfig)(client))))
                .description('For fields that have a date time data type.'),
        })
            .conditional('..dataType', {
            is: joi_1.default.string().valid(...(0, constants_1.vizzlyToPossibleNativeDataTypes)(client, 'boolean')),
            then: joi_1.default.array()
                .items(joi_1.default.string().valid(...(0, support_1.validOperatorsForField)({ dataType: 'boolean' }, (0, getQueryEngineConfig_1.getQueryEngineConfig)(client))))
                .description('For fields that have a boolean data type.'),
        })
            .conditional('..dataType', {
            is: joi_1.default.string().valid(...(0, constants_1.vizzlyToPossibleNativeDataTypes)(client, 'string[]')),
            then: joi_1.default.array()
                .items(joi_1.default.string().valid(...(0, support_1.validOperatorsForField)({ dataType: 'string[]' }, (0, getQueryEngineConfig_1.getQueryEngineConfig)(client))))
                .description('For fields that have a string array data type.'),
        })
            .optional(),
    });
};
exports._fieldSchema = _fieldSchema;
const sqlJoinSchema = joi_1.default.object({
    type: joi_1.default.string()
        .valid(...SUPPORTED_JOINS)
        .required(),
    left: joi_1.default.array().items(joi_1.default.string()).required(),
    right: joi_1.default.array().items(joi_1.default.string()).required(),
    as: joi_1.default.string().optional(),
});
const secureFilterGuardSchema = joi_1.default.object({
    fieldId: joi_1.default.string().required(),
    op: joi_1.default.string()
        .valid(...SUPPORTED_SECURE_FILTER_OPS)
        .required(),
});
const baseDataSetSchema = (fieldSchema) => ({
    id: joi_1.default.string()
        .required()
        .regex(exports.ALLOWED_ID_REGEX, exports.ID_REGEX_ERROR_MESSAGE)
        .description('A unique ID for the data set. This should not change unless the meaning of the data set represents changes.'),
    connectionId: joi_1.default.string().required().description('Reference the connection to use for this data set.'),
    hide: joi_1.default.boolean().optional().description('Hide the data set from the dashboard.'),
    name: joi_1.default.string()
        .required()
        .description('A public name for the data set. This can change as much as you like without causing a breaking change.'),
    description: joi_1.default.string().allow('').optional().max(110).description('A description of what this field represents'),
    sql: joi_1.default.string()
        .optional()
        .description('Optionally define the data set using a SQL statement. If used, the `table` value for each field should equal `vizzly_data_set`.'),
    testParameters: joi_1.default.string()
        .optional()
        .description('Test parameters that are used when creating SQL backed data sets. This is a stringified JSON object.'),
    preAggregated: joi_1.default.alternatives(joi_1.default.array().items(joi_1.default.string()).description('Array of data set IDs relied on by the pre-aggregated data set'), joi_1.default.boolean()
        .allow(false)
        .description('Only `False` is a valid boolean option. For truthy values, provide an array of data set IDs'))
        .optional()
        .description('Optional - For pre-aggregated data sets or SQL views, provide a list of data sets that are relied upon.'),
    fields: joi_1.default.array().min(1).items(fieldSchema).unique('id').message('Field IDs must be unique').required(),
    secureFilterGuards: joi_1.default.array().items(secureFilterGuardSchema).optional(),
});
const _sqlDataSetSchema = (fieldSchema) => joi_1.default.object(Object.assign(Object.assign({}, baseDataSetSchema(fieldSchema)), { joins: joi_1.default.array()
        .items(sqlJoinSchema)
        .unique((a, b) => {
        const rightMatches = JSON.stringify(a['right']) === JSON.stringify(b['right']);
        const noAs = !a['as'] && !b['as'];
        return noAs && rightMatches;
    })
        .message('A table cannot be referenced in a right join multiple times.')
        .optional() }));
exports._sqlDataSetSchema = _sqlDataSetSchema;
/**
 * Return schema to validate a schema. Either
 * alternatives where any is valid. Or a specific one that
 * has been requested.
 * */
const alternativeDataSetSchemas = (options) => {
    var _a;
    // If only one client exists in the list of allowed clients, return that schema for that one client.
    if (((_a = options.onlyClients) === null || _a === void 0 ? void 0 : _a.length) === 1)
        return (0, exports._sqlDataSetSchema)((0, exports._fieldSchema)(options.onlyClients[0]));
    // Build out a schema that validates any of the desired client types in-case we can't be more specific in
    // the validation.
    const fallbackValidations = joi_1.default.alternatives(...(options.onlyClients || constants_1.SUPPORTED_CLIENTS).map((client) => (0, exports._sqlDataSetSchema)((0, exports._fieldSchema)(client))));
    // If a mapping has been provided of data set IDs to client types, then use it to provide a more accurate
    // validation and error message if there is a problem. Not a "this data set could be any of these data sets for 7 different integrations"
    if (options.dataSetIdToClientMapping) {
        const whereClauses = Object.entries(options.dataSetIdToClientMapping).map(([dataSetId, client]) => ({
            is: dataSetId,
            then: (0, exports._sqlDataSetSchema)((0, exports._fieldSchema)(client)),
        }));
        // Build the alternative options, but also provide a 'catch all' in-case the data set mapping fails.
        return joi_1.default.alternatives().conditional('.id', [
            ...whereClauses,
            {
                is: true,
                then: fallbackValidations,
            },
        ]);
    }
    // This really is the end of the road, just provide the broadest validation.
    return fallbackValidations;
};
const configSchema = (fullObj, options) => joi_1.default.object({
    version: joi_1.default.number(),
    connections: joi_1.default.object().pattern(joi_1.default.string(), joi_1.default.object({
        client: joi_1.default.string()
            .allow(...constants_1.SUPPORTED_CLIENTS)
            .required(),
        name: joi_1.default.string().optional().allow(''),
        encryptedCredentials: joi_1.default.string().optional(),
        unencryptedCredentials: joi_1.default.alternatives(joi_1.default.string(), joi_1.default.object()).optional(),
    })),
    dataSets: joi_1.default.array()
        .items(options.checkConnectionSpecificDataSets
        ? joi_1.default.object().custom((value) => {
            var _a, _b;
            const connectionId = value.connectionId || '___vizzly_default_conn';
            const clientType = (_b = (_a = fullObj === null || fullObj === void 0 ? void 0 : fullObj.connections) === null || _a === void 0 ? void 0 : _a[connectionId]) === null || _b === void 0 ? void 0 : _b.client;
            if (!clientType) {
                throw new DataSetNotLinkedToConnection_1.DataSetNotLinkedToConnection(value.id);
            }
            const validationResult = (0, exports._sqlDataSetSchema)((0, exports._fieldSchema)(clientType)).validate(value, joiOptions);
            if (validationResult.error)
                throw validationResult.error;
            return value;
        }, 'Data sets linked to connections')
        : alternativeDataSetSchemas(options))
        .unique('id')
        .message('dataSet[x].id must be unique.')
        .required(),
    executableDashboardConfigForPDF: joi_1.default.string()
        .allow(null)
        .optional()
        .description('JavaScript dashboard config for server-side generated PDFs'),
    publicKeys: joi_1.default.array().items(joi_1.default.string()).optional(),
    sqlViews: joi_1.default.object()
        .pattern(joi_1.default.string(), joi_1.default.object({ name: joi_1.default.string().required(), sql: joi_1.default.string().allow('').required() }))
        .optional(),
});
exports.bigQuerySchema = configSchema({}, { onlyClients: ['bigquery'] });
exports.postgresSchema = configSchema({}, { onlyClients: ['postgres'] });
exports.mariadbSchema = configSchema({}, { onlyClients: ['mariadb'] });
exports.mySQLSchema = configSchema({}, { onlyClients: ['mysql'] });
exports.snowflakeSchema = configSchema({}, { onlyClients: ['snowflake'] });
exports.microsoftSQLServerSchema = configSchema({}, { onlyClients: ['microsoftSQLServer'] });
exports.redshiftSchema = configSchema({}, { onlyClients: ['redshift'] });
exports.default = (config, options = {}) => {
    const validationResult = configSchema(config, Object.assign(Object.assign(Object.assign({}, DEFAULT_SCHEMA_OPTIONS), options), { dataSetIdToClientMapping: buildDataSetToClientTypeMapping(config) })).validate(config, joiOptions);
    if (validationResult.error) {
        validationResult.error.details.map((detail) => {
            var _a, _b;
            if (REDACT_KEYS_FROM_ERROR_MESSAGE.includes(((_a = detail.context) === null || _a === void 0 ? void 0 : _a.key) || '')) {
                if ((_b = detail.context) === null || _b === void 0 ? void 0 : _b.value) {
                    detail.context.value = '-- VIZZLY REDACTED --';
                }
            }
        });
    }
    return validationResult;
};
const buildDataSetToClientTypeMapping = (config) => {
    let dataSetIdToClientMapping = {};
    if (config && typeof (config === null || config === void 0 ? void 0 : config.dataSets) === 'object' && typeof (config === null || config === void 0 ? void 0 : config.connections) === 'object') {
        config.dataSets.forEach((dataSet) => {
            var _a, _b;
            if (dataSetIdToClientMapping) {
                const connectionId = dataSet.connectionId || '___vizzly_default_conn';
                const clientType = (_b = (_a = config === null || config === void 0 ? void 0 : config.connections) === null || _a === void 0 ? void 0 : _a[connectionId]) === null || _b === void 0 ? void 0 : _b.client;
                if (clientType) {
                    dataSetIdToClientMapping[dataSet.id] = clientType;
                }
            }
        });
    }
    return dataSetIdToClientMapping;
};
